import { evaluateExpression } from './routeEngine';

const hashString = (value) => {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  hash += hash << 13;
  hash ^= hash >>> 7;
  hash += hash << 3;
  hash ^= hash >>> 17;
  hash += hash << 5;
  return hash >>> 0;
};

export const deterministicRandom = (seed, ...slots) => (
  hashString([seed, ...slots].join('::')) / 4294967296
);

const localText = (value, locale) => value?.[locale] || value?.fr || value?.en || '';

const weightedChoice = (items, weightOf, random) => {
  const weighted = items
    .map((item) => ({ item, weight: Math.max(0, Number(weightOf(item) || 0)) }))
    .filter(({ weight }) => weight > 0);
  if (!weighted.length) return null;
  const total = weighted.reduce((sum, entry) => sum + entry.weight, 0);
  let cursor = random * total;
  for (const entry of weighted) {
    cursor -= entry.weight;
    if (cursor <= 0) return entry.item;
  }
  return weighted[weighted.length - 1].item;
};

const chooseText = (values, seed, ...slots) => {
  if (!values?.length) return '';
  return values[Math.floor(deterministicRandom(seed, ...slots) * values.length) % values.length];
};

export const createInitialTravelState = (pack) => Object.fromEntries(
  (pack?.stateFields || []).map((field) => [field.id, field.initial]),
);

export const generateTravelParty = ({ pack, seed, size, locale = 'fr', levelId }) => {
  const config = pack.party;
  const count = Math.min(config.maximumSize, Math.max(config.minimumSize, Number(size) || config.defaultSize));
  const names = config.names[locale] || config.names[pack.manifest.defaultLocale] || Object.values(config.names)[0];
  const availableNames = [...names];
  const members = Array.from({ length: count }, (_, index) => {
    const nameIndex = Math.floor(deterministicRandom(seed, 'party-name', index) * availableNames.length);
    const name = availableNames.splice(nameIndex, 1)[0] || `${localText(config.memberLabel, locale)} ${index + 1}`;
    const role = weightedChoice(
      config.roles,
      (item) => item.weight,
      deterministicRandom(seed, 'party-role', index),
    );
    return { id: `member-${index + 1}`, name, roleId: role.id };
  });
  return {
    name: locale === 'fr' ? `Groupe de ${members[0]?.name}` : `${members[0]?.name}'s party`,
    size: count,
    levelId: levelId || config.levels[Math.min(1, config.levels.length - 1)].id,
    members,
  };
};

const clampState = (state, fields) => {
  const next = { ...state };
  fields.forEach((field) => {
    if (field.kind !== 'number') return;
    const numeric = Number(next[field.id]);
    if (!Number.isFinite(numeric)) return;
    next[field.id] = Math.min(
      field.maximum ?? Number.POSITIVE_INFINITY,
      Math.max(field.minimum ?? Number.NEGATIVE_INFINITY, numeric),
    );
  });
  return next;
};

const stateDelta = (before, after) => Object.fromEntries(
  Object.keys(after).filter((key) => after[key] !== before[key]).map((key) => {
    if (typeof before[key] === 'number' && typeof after[key] === 'number') {
      return [key, after[key] - before[key]];
    }
    return [key, after[key]];
  }),
);

export const applyTravelEffects = ({
  effects = [], state, activeThreads = [], tags = [], context, pack, stepIndex,
}) => {
  let nextState = { ...state };
  let nextTags = [...tags];
  let nextThreads = activeThreads.map((thread) => ({ ...thread }));
  effects.forEach((effect) => {
    if (['set', 'add', 'multiply', 'minimum', 'maximum'].includes(effect.operation)) {
      const target = effect.target.replace(/^state\./, '');
      const value = evaluateExpression(effect.value, { ...context, state: nextState });
      const current = nextState[target];
      if (effect.operation === 'set') nextState[target] = value;
      if (effect.operation === 'add') nextState[target] = Number(current || 0) + Number(value || 0);
      if (effect.operation === 'multiply') nextState[target] = Number(current || 0) * Number(value || 0);
      if (effect.operation === 'minimum') nextState[target] = Math.min(Number(current), Number(value));
      if (effect.operation === 'maximum') nextState[target] = Math.max(Number(current), Number(value));
    } else if (effect.operation === 'addTag' && !nextTags.includes(effect.target)) {
      nextTags.push(effect.target);
    } else if (effect.operation === 'removeTag') {
      nextTags = nextTags.filter((tag) => tag !== effect.target);
    } else if (effect.operation === 'scheduleThread') {
      const definition = pack.threads.find((thread) => thread.id === effect.target);
      if (definition && !nextThreads.some((thread) => thread.threadId === definition.id)) {
        nextThreads.push({
          threadId: definition.id,
          sourceStepIndex: stepIndex,
          dueStepIndex: stepIndex + definition.dueAfterSteps,
          attempts: 0,
        });
      }
    } else if (effect.operation === 'resolveThread') {
      nextThreads = nextThreads.filter((thread) => thread.threadId !== effect.target);
    }
  });
  return {
    state: clampState(nextState, pack.stateFields),
    activeThreads: nextThreads,
    tags: nextTags,
  };
};

const routeSteps = (legs, journalSeconds) => {
  const journalHours = journalSeconds / 3600;
  const groups = [];
  let current = [];
  let duration = 0;
  legs.forEach((leg) => {
    if (current.length && duration + leg.durationHours > journalHours * 1.18) {
      groups.push(current);
      current = [];
      duration = 0;
    }
    current.push(leg);
    duration += leg.durationHours;
    if (duration >= journalHours * 0.82) {
      groups.push(current);
      current = [];
      duration = 0;
    }
  });
  if (current.length) groups.push(current);
  return groups;
};

const dominantTerrain = (legs) => {
  const counts = new Map();
  legs.forEach((leg) => counts.set(leg.terrainId, (counts.get(leg.terrainId) || 0) + 1));
  const id = [...counts].sort((first, second) => second[1] - first[1])[0]?.[0] || 'unknown';
  return legs.find((leg) => leg.terrainId === id)?.terrain || { id, tags: [] };
};

const nextEnvironment = ({ pack, currentId, seed, stepIndex }) => {
  const transitions = pack.environment.transitions.filter((item) => item.source === currentId);
  const transition = weightedChoice(
    transitions,
    (item) => item.weight,
    deterministicRandom(seed, stepIndex, 'environment'),
  );
  return transition?.target || currentId || pack.environment.states[0].id;
};

const eventForStep = ({
  pack, context, seed, stepIndex, eventMultiplier, activeThreads,
}) => {
  const due = activeThreads.find((active) => active.dueStepIndex <= stepIndex);
  if (due) {
    const definition = pack.threads.find((thread) => thread.id === due.threadId);
    const returns = deterministicRandom(seed, stepIndex, 'thread', due.threadId, due.attempts)
      <= definition.recurrenceProbability;
    if (returns) {
      const eligible = pack.events.filter((event) => (
        definition.eventIds.includes(event.id) && evaluateExpression(event.when, context)
      ));
      if (eligible.length) {
        return { event: eligible[Math.floor(deterministicRandom(seed, stepIndex, 'thread-event') * eligible.length)], due };
      }
    }
    due.dueStepIndex = stepIndex + 1;
    due.attempts += 1;
  }
  const threadOnlyIds = new Set(pack.threads.flatMap((thread) => thread.eventIds));
  const eligible = pack.events.filter((event) => (
    !threadOnlyIds.has(event.id) && evaluateExpression(event.when, context)
  ));
  const event = weightedChoice(eligible, (item) => {
    const base = Number(evaluateExpression(item.weight, context) || 0);
    return item.kind === 'nothing' ? base / Math.max(0.35, eventMultiplier) : base * eventMultiplier;
  }, deterministicRandom(seed, stepIndex, 'event'));
  return { event, due: null };
};

export const generateTravelRun = ({
  pack,
  route,
  legs,
  party,
  parameters,
  initialState,
  locale = 'fr',
  slotOverrides = {},
}) => {
  if (!pack || !route?.path?.length || !legs?.length || !party) return null;
  const seed = parameters.seed.trim();
  const transport = pack.transports.find((item) => item.id === parameters.transportId);
  const pace = pack.paces.find((item) => item.id === parameters.paceId);
  const danger = pack.dangerLevels.find((item) => item.id === parameters.dangerLevelId);
  const season = pack.seasons.find((item) => item.id === parameters.seasonId);
  const departure = pack.departureConditions.find((item) => item.id === parameters.departureConditionId);
  const partyLevel = pack.party.levels.find((item) => item.id === party.levelId) || pack.party.levels[0];
  const journalUnit = pack.time.units.find((item) => item.id === pack.time.defaultJournalUnit);
  const groupedLegs = routeSteps(legs, journalUnit.seconds);
  let runtime = applyTravelEffects({
    effects: [...(season?.effects || []), ...(departure?.effects || [])],
    state: clampState({ ...initialState }, pack.stateFields),
    activeThreads: [],
    tags: [...(season?.tags || []), ...(departure?.tags || [])],
    context: { state: initialState, transport, pace, danger, party, step: { index: -1 } },
    pack,
    stepIndex: -1,
  });
  const configuredInitialState = clampState({ ...initialState }, pack.stateFields);
  let environmentId = pack.environment.states[0].id;
  let elapsedSeconds = 0;

  const steps = groupedLegs.map((stepLegs, stepIndex) => {
    const eventSeed = slotOverrides[stepIndex]?.event || seed;
    const before = { ...runtime.state };
    const terrain = dominantTerrain(stepLegs);
    environmentId = nextEnvironment({ pack, currentId: environmentId, seed, stepIndex });
    const environment = pack.environment.states.find((item) => item.id === environmentId);
    const baseDurationHours = stepLegs.reduce((sum, leg) => sum + leg.durationHours, 0);
    const distanceMeters = stepLegs.reduce((sum, leg) => sum + leg.distanceMeters, 0);
    const averageDanger = stepLegs.reduce((sum, leg) => sum + leg.dangerCost, 0) / stepLegs.length;
    let context = {
      state: before,
      terrain,
      transport,
      pace,
      danger,
      environment,
      party,
      step: { index: stepIndex, distanceMeters, baseDurationHours },
    };
    const activeModifiers = pack.travelModifiers.filter((modifier) => evaluateExpression(modifier.when, context));
    const durationMultiplier = activeModifiers.reduce(
      (total, modifier) => total * Number(evaluateExpression(modifier.durationMultiplier, context) || 1),
      1,
    );
    const modifierEventMultiplier = activeModifiers.reduce(
      (total, modifier) => total * Number(evaluateExpression(modifier.eventMultiplier, context) || 1),
      1,
    );
    const durationHours = baseDurationHours * durationMultiplier;
    const notableMultiplier = danger.eventMultiplier * partyLevel.eventMultiplier
      * averageDanger * pace.exposureMultiplier * modifierEventMultiplier;
    const mutableThreads = runtime.activeThreads.map((thread) => ({ ...thread }));
    const { event } = eventForStep({
      pack,
      context,
      seed: eventSeed,
      stepIndex,
      eventMultiplier: notableMultiplier,
      activeThreads: mutableThreads,
    });
    runtime = { ...runtime, activeThreads: mutableThreads };
    runtime = applyTravelEffects({
      effects: [...(transport.stepEffects || []), ...(pace.effects || []), ...(event?.effects || [])],
      ...runtime,
      context,
      pack,
      stepIndex,
    });
    context = { ...context, state: runtime.state };
    pack.stateRules.forEach((rule) => {
      if (!evaluateExpression(rule.when, context)) return;
      runtime = applyTravelEffects({ effects: rule.effects, ...runtime, context, pack, stepIndex });
      context = { ...context, state: runtime.state };
    });

    const previousElapsed = elapsedSeconds;
    elapsedSeconds += durationHours * 3600;
    const crossesHalt = Math.floor(previousElapsed / pack.time.haltIntervalSeconds)
      < Math.floor(elapsedSeconds / pack.time.haltIntervalSeconds);
    const halt = crossesHalt
      ? pack.halts.find((item) => evaluateExpression(item.when, context))
      : null;
    if (halt) {
      runtime = applyTravelEffects({ effects: halt.effects, ...runtime, context, pack, stepIndex });
    }
    const after = { ...runtime.state };
    return {
      id: `step-${stepIndex + 1}`,
      index: stepIndex,
      hexKeys: [stepLegs[0].fromKey, ...stepLegs.map((leg) => leg.toKey)],
      distanceMeters,
      baseDurationHours,
      durationHours,
      terrainId: terrain.id,
      networkTypeIds: [...new Set(stepLegs.map((leg) => leg.networkTypeId).filter(Boolean))],
      environmentId,
      atmosphere: chooseText(environment.atmosphere[locale] || environment.atmosphere.fr, seed, stepIndex, 'atmosphere'),
      eventId: event?.id || null,
      eventKind: event?.kind || 'nothing',
      eventNarration: event ? chooseText(event.narration[locale] || event.narration.fr, eventSeed, stepIndex, 'event-text', event.id) : '',
      haltId: halt?.id || null,
      haltNarration: halt ? chooseText(halt.narration[locale] || halt.narration.fr, seed, stepIndex, 'halt-text', halt.id) : '',
      activeModifierIds: activeModifiers.map((modifier) => modifier.id),
      stateBefore: before,
      stateAfter: after,
      stateDelta: stateDelta(before, after),
      activeThreadsAfter: runtime.activeThreads.map((thread) => ({ ...thread })),
      slotSeeds: {
        environment: `${seed}:${stepIndex}:environment`,
        event: eventSeed,
        halt: `${seed}:${stepIndex}:halt`,
      },
    };
  });

  return {
    engineVersion: '1',
    status: 'generated',
    seed,
    routeProfileId: route.profileId,
    routePath: route.path,
    party,
    parameters,
    initialState: configuredInitialState,
    currentState: steps[0]?.stateBefore || configuredInitialState,
    currentStepIndex: 0,
    activeThreads: [],
    steps,
    generatedAt: new Date().toISOString(),
  };
};
